'use client'

import { useCallback, useSyncExternalStore } from 'react'
import type { QueryClient } from '@tanstack/react-query'

const STORAGE_KEY = 'trellis-auth-cache-scope'
const OWNER_KEY = 'trellis-auth-cache-owner'
const listeners = new Set<() => void>()
let browserScope: string | null = null
let browserOwnerId: string | null = null
// undefined means auth has not resolved; null means an observed signed-out
// session. Keep this separate from query data, which logout clears and late
// server hydration can overwrite. Server renders have distinct QueryClients.
const queryIdentities = new WeakMap<QueryClient, string | null>()
const queryIdentityListeners = new WeakMap<QueryClient, Set<() => void>>()

export function setAuthQueryIdentity(client: QueryClient, userId: string | null): void {
  if (queryIdentities.get(client) === userId) {
    return
  }
  queryIdentities.set(client, userId)
  for (const listener of queryIdentityListeners.get(client) ?? []) {
    listener()
  }
}

const getServerQueryIdentity = (): undefined => undefined

export function getAuthQueryIdentity(client: QueryClient): string | null | undefined {
  return queryIdentities.get(client)
}

export function useAuthQueryIdentity(client: QueryClient): string | null | undefined {
  const subscribeIdentity = useCallback(
    (listener: () => void) => {
      let subscribers = queryIdentityListeners.get(client)
      if (!subscribers) {
        subscribers = new Set()
        queryIdentityListeners.set(client, subscribers)
      }
      subscribers.add(listener)
      return () => {
        subscribers.delete(listener)
      }
    },
    [client]
  )
  const getIdentity = useCallback(() => queryIdentities.get(client), [client])
  return useSyncExternalStore(subscribeIdentity, getIdentity, getServerQueryIdentity)
}

function createScope(): string {
  return globalThis.crypto.randomUUID()
}

function getBrowserScope(): string | null {
  if (typeof window === 'undefined') {
    return null
  }
  if (browserScope) {
    return browserScope
  }

  try {
    const storedScope = window.sessionStorage.getItem(STORAGE_KEY)
    if (storedScope) {
      browserScope = storedScope
      return browserScope
    }

    browserScope = createScope()
    window.sessionStorage.setItem(STORAGE_KEY, browserScope)
    return browserScope
  } catch {
    browserScope = createScope()
    return browserScope
  }
}

function getServerScope(): null {
  return null
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function rotateAuthCacheScope(): void {
  if (typeof window === 'undefined') {
    return
  }
  browserScope = createScope()
  browserOwnerId = null
  try {
    window.sessionStorage.setItem(STORAGE_KEY, browserScope)
    window.sessionStorage.removeItem(OWNER_KEY)
  } catch {
    // The in-memory scope still rotates when session storage is unavailable.
  }
  for (const listener of listeners) {
    listener()
  }
}

export function syncAuthCacheScopeOwner(userId: string): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    browserOwnerId = browserOwnerId ?? window.sessionStorage.getItem(OWNER_KEY)
  } catch {
    // The in-memory owner still protects this browser document.
  }

  if (browserOwnerId && browserOwnerId !== userId) {
    rotateAuthCacheScope()
  }
  browserOwnerId = userId
  try {
    window.sessionStorage.setItem(OWNER_KEY, userId)
  } catch {
    // The in-memory owner remains authoritative when storage is unavailable.
  }
}

export function useAuthCacheScope(): string | null {
  return useSyncExternalStore(subscribe, getBrowserScope, getServerScope)
}
